import React, { Fragment, JSX, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { getStandardsBySlideId } from "../../../api/standard.service";
import { IStandard } from "../../../model/interface/standard";
import { ISlide } from "../../../model/interface/slide";
import {
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  LinkIcon,
} from "../../../assets/icons/index";
import { toast } from "react-toastify";
import Loader from "../../shared/loader/loader";
import Constant from "../../../utils/constant";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Pagination from "../../shared/common/pagination";
import ConfirmationPopup from "../../shared/common/confirmationPopup";
import { unassociateSlideStandards } from "../../../api/slide.service";
import StandardSearch from "../../shared/common/standardSearch";

interface SlideProps {
  slideData?: ISlide;
}

function SlideStandards(props?: SlideProps): JSX.Element {
  const { slideId: urlSlideId } = useParams<{ slideId: string }>();
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [standards, setStandards] = useState<IStandard[]>([]);
  const [selectedStandards, setSelectedStandards] = useState<Array<number>>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [orderByColumn, setOrderByColumn] = useState<string>("name");
  const [sortDir, setSortDir] = useState<string>("ASC");
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const slideId =
    props?.slideData?.slideId || (urlSlideId ? parseInt(urlSlideId) : 0);

  function handleSort(columnName: string) {
    setLoading(true);
    setOrderByColumn(columnName);
    setSortAscending(!sortAscending);
    setSortDir(sortAscending ? "DESC" : "ASC");
  }

  useEffect(() => {
    if (isLoaded) {
      getStandards();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      getStandards();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  useEffect(() => {
    if (isLoaded) {
      getStandards();
    }
  }, [sortDir]);

  useEffect(() => {
    if (slideId && slideId > 0) {
      getStandards();
      setIsLoaded(true);
    }
  }, [slideId]);

  const getStandards = () => {
    if (!slideId || slideId <= 0) return;

    setLoading(true);

    getStandardsBySlideId(slideId, pageSize, pageNumber, orderByColumn, sortDir)
      .then((data) => {
        setStandards(data?.data?.data || []);
        setTotalPages(Math.ceil((data?.data?.totalRecords || 0) / pageSize));
        setTotalRecords(data?.data?.totalRecords || 0);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Failed to fetch standards");
      });
  };

  const handleSelectAll = (isChecked: boolean) => {
    const allStandards = [...standards];
    allStandards.forEach((standard) => {
      standard["isSelected"] = isChecked;
    });
    setStandards(allStandards);
    setSelectedStandards(allStandards.map((standard) => standard.standardId));
    setSelectAll(isChecked);
  };

  const handleSelectStandard = (isChecked: boolean, standardId: number) => {
    const allStandards = [...standards];
    allStandards.forEach((standard) => {
      if (standard.standardId === standardId)
        standard["isSelected"] = isChecked;
    });
    if (!isChecked && selectAll) {
      setSelectAll(false);
    }
    setStandards(allStandards);
    const selected = allStandards
      .filter((standard) => standard["isSelected"])
      .map((standard) => standard.standardId);
    setSelectedStandards(selected);
    if (selected.length === allStandards.length) {
      setSelectAll(true);
    }
  };

  const handleDelinkStandard = (confirmAction: boolean) => {
    setShowConfirmation(false);
    if (confirmAction) {
      if (selectedStandards && selectedStandards.length > 0) {
        setLoading(true);
        unassociateSlideStandards(slideId, selectedStandards)
          .then(() => getStandards())
          .catch((_) => {
            setLoading(false);
            toast.error("Cannot unassociate standards");
          });
      }
    }
  };

  if (!slideId || slideId <= 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">
          Please save the slide first before managing standards.
        </p>
      </div>
    );
  }

  return (
    <Fragment>
      {loading && <Loader />}
      {showConfirmation && (
        <ConfirmationPopup
          title={"Unassociate Standards"}
          confirmAction={handleDelinkStandard}
          message={
            "Are you sure you want to unassociate it? It will remove the slide from all linked standards."
          }
          buttonText={"Yes"}
        />
      )}
      <div className="text-gray-900 block px-4 py-4 text-sm shadow border-b border-gray-200 rounded-b-lg">
        <StandardSearch
          paramType="slide"
          paramId={slideId}
          updateStandardsList={getStandards}
        ></StandardSearch>
        <div className="flex justify-end mb-6">
          <button
            type="button"
            onClick={(e) => {
              if (selectedStandards && selectedStandards.length > 0) {
                setShowConfirmation(true);
              }
            }}
            className="mt-2 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
          >
            Unassociate
          </button>
        </div>
        <div id="dataContainer" className="mt-4">
          <div className="flex flex-col mt-1">
            <div className="-my-2 sm:-mx-6 lg:-mx-8">
              <div className="align-middle inline-block min-w-full sm:px-6 lg:px-8">
                <div className="shadow overflow-x-auto border-b border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-primary-color">
                      <tr>
                        <th></th>
                        <th className="pl-5 relative">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer text-primary-violet border-gray-300 focus:ring-primary-violet"
                            onChange={(e) => {
                              handleSelectAll(e.target.checked);
                            }}
                            checked={selectAll}
                          />
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                        >
                          <span className="flex gap-2">
                            Subject
                            <span className="cursor-pointer">
                              {!sortAscending &&
                              orderByColumn === "SubjectName" ? (
                                <CaretUpSolidIcon
                                  onClick={() => handleSort("SubjectName")}
                                  className="w-5 h-5"
                                ></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon
                                  onClick={() => handleSort("SubjectName")}
                                  className="w-5 h-5"
                                ></CaretDownSolidIcon>
                              )}
                            </span>
                          </span>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                        >
                          <span className="flex gap-2">
                            State
                            <span className="cursor-pointer">
                              {!sortAscending &&
                              orderByColumn === "StateName" ? (
                                <CaretUpSolidIcon
                                  onClick={() => handleSort("StateName")}
                                  className="w-5 h-5"
                                ></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon
                                  onClick={() => handleSort("StateName")}
                                  className="w-5 h-5"
                                ></CaretDownSolidIcon>
                              )}
                            </span>
                          </span>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                        >
                          <span className="flex gap-2">
                            Domain
                            <span className="cursor-pointer">
                              {!sortAscending &&
                              orderByColumn === "DomainName" ? (
                                <CaretUpSolidIcon
                                  onClick={() => handleSort("DomainName")}
                                  className="w-5 h-5"
                                ></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon
                                  onClick={() => handleSort("DomainName")}
                                  className="w-5 h-5"
                                ></CaretDownSolidIcon>
                              )}
                            </span>
                          </span>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                        >
                          <span className="flex gap-2">
                            Standard
                            <span className="cursor-pointer">
                              {!sortAscending && orderByColumn === "Code" ? (
                                <CaretUpSolidIcon
                                  onClick={() => handleSort("Code")}
                                  className="w-5 h-5"
                                ></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon
                                  onClick={() => handleSort("Code")}
                                  className="w-5 h-5"
                                ></CaretDownSolidIcon>
                              )}
                            </span>
                          </span>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white dark:text-white  "
                        >
                          Description
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                        ></th>
                      </tr>
                    </thead>
                    <tbody>
                      {standards.map((standard: any, index) => (
                        <tr
                          key={standard.standardId}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }
                        >
                          <td className="pl-5 relative">
                            {standard.isActive ? (
                              <span className="mr-1 h-4/5 w-0.5  bg-green-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                            ) : (
                              <span className="mr-1 h-4/5 w-0.5  bg-red-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                            )}
                            {standard.isLinked && (
                              <LinkIcon
                                className={`cursor-not-allowed w-5 ${
                                  standard.isLinked ? "" : "opacity-50"
                                }`}
                              />
                            )}
                          </td>
                          <td className="pl-5 relative">
                            <input
                              type="checkbox"
                              className="h-4 w-4 cursor-pointer text-primary-violet border-gray-300 focus:ring-primary-violet"
                              checked={standard["isSelected"]}
                              onChange={(e) => {
                                handleSelectStandard(
                                  e.target.checked,
                                  standard.standardId
                                );
                              }}
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                            <Link
                              className={`ml-1 cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline`}
                              to={Constant.SUBJECTDETAIL.replace(
                                ":id",
                                standard.subjectId.toString()
                              )}
                            >
                              {standard.subjectName}
                            </Link>
                          </td>
                          <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                            {standard.stateNames}
                          </td>

                          <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                            <Link
                              to={Constant.EDITDOMAIN.replace(
                                ":id",
                                standard.domainId
                                  ? standard.domainId.toString()
                                  : ""
                              )}
                              className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                            >
                              {standard.domainName}
                            </Link>
                          </td>
                          <td className="px-3 py-2  pl-6 whitespace-wrap text-sm text-left font-normal text-gray-500">
                            <Link
                              to={Constant.EDITSTANDARD.replace(
                                ":id",
                                standard.standardId
                                  ? standard.standardId.toString()
                                  : ""
                              )}
                              className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                            >
                              {standard.standardIcon && (
                                <img
                                  className="h-5 w-5 mr-2"
                                  src={standard.standardIcon}
                                />
                              )}
                              [{standard.code}] {standard.name}
                            </Link>
                          </td>
                          <td className="px-3 py-2 whitespace-wrap text-sm font-normal text-gray-500">
                            {standard?.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div
              className={`flex justify-between items-center ${
                standards.length === 0 ? "hidden" : ""
              }`}
            >
              <Pagination
                pages={totalPages}
                setPageNumber={setPageNumber}
                ref={paginationRef}
              />
              <span>
                <span className="text-sm pr-4 ml-auto text-left-700">
                  Showing {pageNumber} to{" "}
                  {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                  {totalRecords} results
                </span>

                <SelectPageSize
                  setPageSize={setPageSize}
                  type={"second"}
                  ref={pageSizeRef}
                />
              </span>
            </div>

            {standards.length === 0 && <p className="p-2">No Records Found</p>}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default SlideStandards;
